'use client';

import { Content, asText, isFilled } from '@prismicio/client';
import { PrismicRichText, SliceComponentProps } from '@prismicio/react';
import Image from 'next/image';
import { useEffect, useRef, useState } from 'react';
import HighlightedText from '@/components/HighlightedText/HighlightedText';

/**
 * Props for `HeadlineCenteredIllustration`.
 */
export type HeadlineCenteredIllustrationProps =
  SliceComponentProps<Content.HeadlineCenteredIllustrationSlice>;

/**
 * Component for "HeadlineCenteredIllustration" Slices.
 */
const HeadlineCenteredIllustration = ({
  slice,
}: HeadlineCenteredIllustrationProps): JSX.Element => {
  const headlineText = asText(slice.primary.headline) || '';
  const hasSubheadline = isFilled.richText(slice.primary.subheadline);
  const isDefaultVariation = slice.variation === 'default';

  const [isLottieLoaded, setIsLottieLoaded] = useState(false);
  const [isInView, setIsInView] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const playerContainerRef = useRef<HTMLDivElement>(null);
  const sectionRef = useRef<HTMLDivElement>(null);

  // Detect mobile device
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  let lottieSrc: string | undefined;
  let hasIllustration = false;

  if (isDefaultVariation) {
    const defaultPrimary =
      slice.primary as Content.HeadlineCenteredIllustrationSliceDefault['primary'];

    const hasLottie = defaultPrimary.lottie === true;
    const assetLinkUrl =
      hasLottie && isFilled.link(defaultPrimary.asset_link)
        ? (defaultPrimary.asset_link.url ?? undefined)
        : undefined;

    lottieSrc =
      assetLinkUrl && assetLinkUrl.toLowerCase().endsWith('.json')
        ? assetLinkUrl
        : undefined;

    hasIllustration = isFilled.image(defaultPrimary.illustration);
  }

  // Use Intersection Observer to detect when component is near viewport
  useEffect(() => {
    if (!lottieSrc || !sectionRef.current) {
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          setIsInView(true);
        }
      },
      {
        rootMargin: '200px', // Start loading 200px before component enters viewport
        threshold: 0,
      },
    );

    observer.observe(sectionRef.current);

    return () => {
      observer.disconnect();
    };
  }, [lottieSrc]);

  // Only load Lottie when component is in view and after a delay
  useEffect(() => {
    if (!lottieSrc || !isInView) {
      return;
    }

    const loadLottie = () => {
      import('@lottiefiles/lottie-player').then(() => {
        setIsLottieLoaded(true);
      });
    };

    // Shorter delay on desktop, longer on mobile where resources are constrained
    const delay = isMobile ? 1500 : 500;
    const timeout = setTimeout(loadLottie, delay);
    return () => clearTimeout(timeout);
  }, [lottieSrc, isInView, isMobile]);

  // Create the lottie-player element dynamically once loaded
  useEffect(() => {
    if (isLottieLoaded && playerContainerRef.current && lottieSrc) {
      const container = playerContainerRef.current;
      const player = document.createElement('lottie-player');
      player.setAttribute('autoplay', '');
      player.setAttribute('loop', '');
      player.setAttribute('mode', 'normal');
      player.setAttribute('background', 'transparent');
      player.setAttribute('src', lottieSrc);
      player.style.width = '100%';
      player.style.height = '100%';

      container.appendChild(player);

      return () => {
        if (container.contains(player)) {
          container.removeChild(player);
        }
      };
    }
  }, [isLottieLoaded, lottieSrc]);

  if (!isDefaultVariation) {
    return (
      <section
        data-slice-type={slice.slice_type}
        data-slice-variation={slice.variation}
        className="relative text-center"
      >
        <div className="container mx-auto px-4 md:px-6 flex flex-col items-center text-center gap-12">
          <div className="max-w-4xl flex flex-col gap-2">
            {headlineText && (
              <h2 className="text-4xl md:text-5xl font-bold text-white">
                <HighlightedText
                  text={headlineText}
                  highlightClassName="text-[#05F273]"
                />
              </h2>
            )}
            {hasSubheadline && (
              <PrismicRichText
                field={slice.primary.subheadline || []}
                components={{
                  heading2: ({ children }) => (
                    <h2
                      className="text-4xl md:text-5xl font-bold mb-8"
                      style={{ color: '#05F273' }}
                    >
                      {children}
                    </h2>
                  ),
                }}
              />
            )}
          </div>
          {/* TODO: Add Animated Scroll Here */}
        </div>
      </section>
    );
  }

  return (
    <section
      ref={sectionRef}
      data-slice-type={slice.slice_type}
      data-slice-variation={slice.variation}
      className="relative text-center bg-[#0F423A]"
    >
      <div className="container mx-auto py-6 md:py-0 px-4 md:px-6 flex flex-col items-center text-center gap-12">
        <div className="max-w-4xl flex flex-col gap-2">
          {headlineText && (
            <h2 className="text-4xl md:text-5xl font-bold text-white font-inter">
              <HighlightedText
                text={headlineText}
                highlightClassName="text-[#05F273]"
              />
            </h2>
          )}
          {hasSubheadline && (
            <PrismicRichText
              field={slice.primary.subheadline || []}
              components={{
                heading2: ({ children }) => (
                  <h2
                    className="text-4xl md:text-5xl font-bold mb-8 font-inter"
                    style={{ color: '#05F273' }}
                  >
                    {children}
                  </h2>
                ),
              }}
            />
          )}
        </div>

        {/* Lottie animation or fallback illustration */}
        {lottieSrc ? (
          <div
            ref={playerContainerRef}
            className="mx-auto w-full max-w-xl min-h-[400px]"
          />
        ) : hasIllustration ? (
          <Image
            src={slice.primary.illustration.url!}
            alt={slice.primary.illustration.alt || 'Illustration'}
            width={slice.primary.illustration.dimensions?.width}
            height={slice.primary.illustration.dimensions?.height}
            className="mx-auto"
          />
        ) : null}
      </div>
      {/* Bottom Gradient Fade */}
      <div className="absolute bottom-0 left-0 w-full h-52 md:h-48 z-20 bg-gradient-to-b from-transparent to-[#0E1010] pointer-events-none" />
    </section>
  );
};

export default HeadlineCenteredIllustration;
